import React, { useState, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
// Context
import { useAuth } from 'AuthContext';
import useClubContext from 'contexts/Club';
import BooksContext from './BooksContext';
// Services
import { likeBook, unlikeBook } from 'services/books/books.service';
import { recordInteraction, removeInteraction } from 'services/books/discover.service';
import { updateUserBookProgress } from 'services/progress/progress.service';
// Hooks
import { useBooks, useCreateBook, useUpdateBook, useDeleteBook } from 'hooks/useBooks';

const BOOKS_VIEW_STORAGE_KEY_PREFIX = 'books-view';

const DEFAULT_BOOK_FILTERS = {
  theme: 'all',
  suggestedBy: 'all',
  listScope: 'backlog',
  readByMe: false,
  suggestedByMe: false,
  likedByMe: false,
  bookmarkedOnly: false,
};

/** Normalize persisted filters (handles legacy listScope bookmarked + status read). */
const normalizeStoredFilters = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_BOOK_FILTERS };
  }
  const lsRaw = String(raw.listScope ?? 'backlog').trim();
  let listScope = ['chosen', 'backlog', 'suggested'].includes(lsRaw) ? lsRaw : 'backlog';
  let bookmarkedOnly = Boolean(raw.bookmarkedOnly);
  if (lsRaw === 'bookmarked') {
    listScope = 'suggested';
    bookmarkedOnly = true;
  }
  const legacyStatus = String(raw.status ?? 'all');
  let readByMe = Boolean(raw.readByMe);
  if (legacyStatus === 'read') {
    readByMe = true;
  }
  if (bookmarkedOnly && listScope !== 'suggested') {
    listScope = 'suggested';
  }
  return {
    theme: String(raw.theme ?? 'all'),
    suggestedBy: String(raw.suggestedBy ?? 'all'),
    listScope,
    readByMe,
    suggestedByMe: Boolean(raw.suggestedByMe),
    likedByMe: Boolean(raw.likedByMe),
    bookmarkedOnly,
  };
};

const getStoredBooksViewState = (clubId) => {
  if (typeof window === 'undefined' || !clubId) return null;
  try {
    const raw = sessionStorage.getItem(`${BOOKS_VIEW_STORAGE_KEY_PREFIX}-${clubId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      pagination: parsed.pagination && typeof parsed.pagination.page === 'number' && parsed.pagination.page >= 1
        ? { page: parsed.pagination.page, pageSize: Number(parsed.pagination.pageSize) || 10 }
        : { page: 1, pageSize: 10 },
      filters: normalizeStoredFilters(parsed.filters),
      search: typeof parsed.search === 'string' ? parsed.search : '',
      sort: parsed.sort && typeof parsed.sort === 'object'
        ? { field: String(parsed.sort.field ?? 'createdAt'), direction: String(parsed.sort.direction ?? 'desc') }
        : { field: 'createdAt', direction: 'desc' },
    };
  } catch {
    return null;
  }
};

const setStoredBooksViewState = (clubId, state) => {
  if (typeof window === 'undefined' || !clubId || !state) return;
  try {
    sessionStorage.setItem(`${BOOKS_VIEW_STORAGE_KEY_PREFIX}-${clubId}`, JSON.stringify(state));
  } catch {
    // ignore quota or parse errors
  }
};

const BOOKS_LIST_STORAGE_KEY = 'books_list';

const getStoredBooksList = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(BOOKS_LIST_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const books = Array.isArray(parsed.books) ? parsed.books : [];
    return {
      clubId: parsed.clubId,
      userId: parsed.userId,
      books,
      totalCount: typeof parsed.totalCount === 'number' ? parsed.totalCount : 0,
    };
  } catch {
    return null;
  }
};

const setStoredBooksList = (clubId, userId, payload) => {
  if (typeof window === 'undefined' || !clubId || !userId || !payload) return;
  try {
    sessionStorage.setItem(BOOKS_LIST_STORAGE_KEY, JSON.stringify({
      clubId,
      userId,
      books: Array.isArray(payload.books) ? payload.books : [],
      totalCount: typeof payload.totalCount === 'number' ? payload.totalCount : 0,
    }));
  } catch {
    // ignore quota or parse errors
  }
};

let _booksRestoreCache = null;

// Simple debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const BooksProvider = ({ children }) => {
  // ******************STATE VALUES**********************
  const { user } = useAuth();
  const { currentClub } = useClubContext();

  const queryClient = useQueryClient();
  const lastRestoredClubIdRef = useRef(null);

  // New state for filters and pagination (restored from sessionStorage when club is set)
  const [pagination, setPaginationState] = useState({ page: 1, pageSize: 10 });
  const [filters, setFiltersState] = useState({ ...DEFAULT_BOOK_FILTERS });
  const [search, setSearchState] = useState('');
  const [sort, setSortState] = useState({ field: 'createdAt', direction: 'desc' });

  // Restore books view state from sessionStorage when mounting with a club (e.g. after switching back from Feed)
  useEffect(() => {
    const clubId = currentClub?.id;
    if (!clubId || lastRestoredClubIdRef.current === clubId) return;
    lastRestoredClubIdRef.current = clubId;
    const stored = getStoredBooksViewState(clubId);
    if (stored) {
      setPaginationState(stored.pagination);
      setFiltersState(stored.filters);
      setSearchState(stored.search);
      setSortState(stored.sort);
    }
  }, [currentClub?.id]);

  // Persist books view state to sessionStorage when it changes (so we can restore when user returns to Books tab)
  useEffect(() => {
    const clubId = currentClub?.id;
    if (!clubId) return;
    setStoredBooksViewState(clubId, {
      pagination,
      filters,
      search,
      sort,
    });
  }, [currentClub?.id, pagination, filters, search, sort]);

  // Debounce search term (500ms delay)
  const debouncedSearch = useDebounce(search, 500);

  const queryOptions = useMemo(() => ({
    page: pagination.page,
    pageSize: pagination.pageSize,
    filters,
    search: debouncedSearch,
    sort,
  }), [pagination, filters, debouncedSearch, sort]);

  const booksQueryKey = useMemo(() => (
    ['books', currentClub?.id, user?.uid, queryOptions]
  ), [currentClub?.id, user?.uid, queryOptions]);

  const {
    data,
    isLoading,
    error: queryError,
    refetch,
  } = useBooks(currentClub?.id, user?.uid, queryOptions);

  const [displayBooks, setDisplayBooks] = useState(() => {
    if (!_booksRestoreCache) _booksRestoreCache = getStoredBooksList();
    return _booksRestoreCache?.books ?? [];
  });
  const [displayTotalCount, setDisplayTotalCount] = useState(() => _booksRestoreCache?.totalCount ?? 0);

  // Sync display state from query data when available (React Query remains source of truth)
  useEffect(() => {
    if (data != null) {
      setDisplayBooks(Array.isArray(data.books) ? data.books : []);
      setDisplayTotalCount(typeof data.totalCount === 'number' ? data.totalCount : 0);
    }
  }, [data]);

  // Clear restored state if stored list was for a different club/user
  useEffect(() => {
    if (!currentClub?.id || !user?.uid) return;
    const stored = getStoredBooksList();
    if (stored && (stored.clubId !== currentClub.id || stored.userId !== user.uid)) {
      setDisplayBooks([]);
      setDisplayTotalCount(0);
    }
  }, [currentClub?.id, user?.uid]);

  // Persist books list so we can restore on remount (avoid empty flash)
  useEffect(() => {
    if (!currentClub?.id || !user?.uid) return;
    setStoredBooksList(currentClub.id, user.uid, { books: displayBooks, totalCount: displayTotalCount });
  }, [currentClub?.id, user?.uid, displayBooks, displayTotalCount]);

  useEffect(() => () => { _booksRestoreCache = null }, []);

  const books = displayBooks;
  const totalCount = displayTotalCount;
  const totalPages = Math.ceil(totalCount / pagination.pageSize);
  const error = queryError?.message || (queryError ? 'Failed to fetch books' : null);
  const loading = isLoading;

  const createBookMutation = useCreateBook(currentClub?.id);
  const updateBookMutation = useUpdateBook(currentClub?.id);
  const deleteBookMutation = useDeleteBook(currentClub?.id);

  // ******************SETTERS FOR STATE**********************
  const setPage = (page) => {
    setPaginationState(prev => ({ ...prev, page }));
  };

  const setPageSize = (pageSize) => {
    setPaginationState(prev => ({ ...prev, pageSize, page: 1 }));
  };

  const setFilters = (newFilters) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }));
    setPaginationState(prev => ({ ...prev, page: 1 }));
  };

  const setSearch = (term) => {
    setSearchState(term);
    setPaginationState(prev => ({ ...prev, page: 1 }));
  };

  const setSort = (field, direction) => {
    setSortState({ field, direction });
    setPaginationState(prev => ({ ...prev, page: 1 }));
  };

  // ******************LOAD FUNCTIONS**********************
  const refreshBooks = useCallback(() => {
    return refetch();
  }, [refetch]);

  // ******************ACTIONS**********************
  const createBook = async (bookData) => {
    if (!currentClub) throw new Error('No club selected');
    if (!user?.uid) throw new Error('User not logged in');

    try {
      const newBook = await createBookMutation.mutateAsync({
        bookData,
        userId: user.uid,
      });

      queryClient.setQueryData(booksQueryKey, (old) => {
        const existing = old || { books: [], totalCount: 0 };
        return {
          ...existing,
          books: [{
            ...newBook,
            likesCount: 0,
            isLiked: false,
            superLikesCount: 0,
            isSuperLiked: false,
          }, ...(existing.books || [])],
          totalCount: (existing.totalCount || 0) + 1,
        };
      });

      return newBook;
    } catch (err) {
      console.error('Error creating book:', err);
      throw err;
    }
  };

  const updateBook = async (bookId, updates) => {
    if (!currentClub) throw new Error('No club selected');

    try {
      const updatedBook = await updateBookMutation.mutateAsync({ bookId, updates });

      queryClient.setQueryData(booksQueryKey, (old) => {
        if (!old || !old.books) return old;
        return {
          ...old,
          books: old.books.map(book =>
            book.id === bookId ? { ...book, ...updatedBook } : book,
          ),
        };
      });
      
      return updatedBook;
    } catch (err) {
      console.error('Error updating book:', err);
      throw err;
    }
  };

  const deleteBook = async (bookId) => {
    if (!currentClub) throw new Error('No club selected');

    try {
      await deleteBookMutation.mutateAsync(bookId);

      queryClient.setQueryData(booksQueryKey, (old) => {
        if (!old || !old.books) return old;
        return {
          ...old,
          books: old.books.filter(book => book.id !== bookId),
          totalCount: Math.max(0, (old.totalCount || 0) - 1),
        };
      });
    } catch (err) {
      console.error('Error deleting book:', err);
      throw err;
    }
  };

  const updateBookProgress = async (bookId, progressData) => {
    if (!user) throw new Error('User not logged in');
    
    // Optimistic update
    queryClient.setQueryData(booksQueryKey, (old) => {
      if (!old || !old.books) return old;
      return {
        ...old,
        books: old.books.map(book => {
          if (book.id === bookId) {
            const currentProgress = book.progress || {};
            return {
              ...book,
              progress: {
                ...currentProgress,
                ...progressData,
                updatedAt: new Date().toISOString(),
              },
            };
          }
          return book;
        }),
      };
    });

    try {
      await updateUserBookProgress(user.uid, bookId, progressData);
      // We could re-fetch or assume optimistic update is correct
    } catch (err) {
      console.error('Error updating progress:', err);
      // Revert on error could be implemented here if strictly necessary
      refreshBooks(); // Re-fetch to sync state on error
      throw err;
    }
  };

  const toggleBookLike = async (bookId, shouldLike) => {
    if (!user || !currentClub) throw new Error('User not logged in');

    queryClient.setQueryData(booksQueryKey, (old) => {
      if (!old || !old.books) return old;
      return {
        ...old,
        books: old.books.map(book => {
          if (book.id !== bookId) return book;
          const likesCount = Math.max(0, (book.likesCount || 0) + (shouldLike ? 1 : -1));
          return {
            ...book,
            isLiked: shouldLike,
            likesCount,
          };
        }),
      };
    });

    try {
      if (shouldLike) {
        await likeBook(currentClub.id, bookId, user.uid);
      } else {
        await unlikeBook(currentClub.id, bookId, user.uid);
      }
    } catch (err) {
      console.error('Error toggling book like:', err);
      refreshBooks();
      throw err;
    }
  };

  /**
   * Super-like uses discover interactions (book_interactions).
   * Updates pool / promotion per server rules; merges counts from API responses.
   */
  const toggleBookSuperLike = async (bookId, shouldSuperLike) => {
    if (!user || !currentClub) throw new Error('User not logged in');

    queryClient.setQueryData(booksQueryKey, (old) => {
      if (!old || !old.books) return old;
      return {
        ...old,
        books: old.books.map((b) => {
          if (b.id !== bookId) return b;
          const delta = shouldSuperLike ? 1 : -1;
          return {
            ...b,
            isSuperLiked: shouldSuperLike,
            superLikesCount: Math.max(0, (b.superLikesCount || 0) + delta),
          };
        }),
      };
    });

    try {
      if (shouldSuperLike) {
        const res = await recordInteraction(currentClub.id, bookId, user.uid, 'super_like');
        const nb = res?.book || {};
        queryClient.setQueryData(booksQueryKey, (old) => {
          if (!old || !old.books) return old;
          return {
            ...old,
            books: old.books.map((b) => {
              if (b.id !== bookId) return b;
              return {
                ...b,
                isSuperLiked: true,
                superLikesCount: nb.superLikesCount ?? b.superLikesCount,
                pool: nb.pool ?? b.pool,
                promotedAt: nb.promotedAt ?? b.promotedAt,
                revalidationRequestedAt: nb.revalidationRequestedAt ?? b.revalidationRequestedAt,
              };
            }),
          };
        });
        return { remainingSuperLikes: res?.remainingSuperLikes };
      }
      const res = await removeInteraction(currentClub.id, bookId, user.uid, 'super_like');
      const nb = res?.book || {};
      queryClient.setQueryData(booksQueryKey, (old) => {
        if (!old || !old.books) return old;
        return {
          ...old,
          books: old.books.map((b) => {
            if (b.id !== bookId) return b;
            return {
              ...b,
              isSuperLiked: false,
              superLikesCount: nb.superLikesCount ?? Math.max(0, (b.superLikesCount || 0) - 1),
              pool: nb.pool ?? b.pool,
              revalidationRequestedAt: nb.revalidationRequestedAt ?? b.revalidationRequestedAt,
            };
          }),
        };
      });
      return { remainingSuperLikes: res?.remainingSuperLikes };
    } catch (err) {
      console.error('Error toggling super like:', err);
      refreshBooks();
      throw err;
    }
  };

  // ******************EXPORTS**********************
  return (
    <BooksContext.Provider
      value={{
        // State
        books,
        loading,
        error,
        totalCount,
        totalPages,
        pagination,
        filters,
        search,
        sort,

        // Setters
        setPage,
        setPageSize,
        setFilters,
        setSearch,
        setSort,

        // Actions
        createBook,
        updateBook,
        deleteBook,
        updateBookProgress,
        toggleBookLike,
        toggleBookSuperLike,
        refreshBooks,
      }}
    >
      {children}
    </BooksContext.Provider>
  );
};

export const useBooksContext = () => {
  const context = useContext(BooksContext);
  if (!context) {
    throw new Error('useBooksContext must be used within a BooksProvider');
  }
  return context;
};

export default BooksProvider;
